import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from 'recharts';
import { aiApi } from '../api';

const card = { background: '#1e293b', borderRadius: 12, padding: 24, marginBottom: 20 };
const SYMBOLS = ['BTCUSDT', 'ETHUSDT', 'BNBUSDT', 'SOLUSDT'];
const INTERVALS = ['1m', '5m', '15m', '30m', '1h', '4h', '1d'];

const signalColor = (s) => s === 'buy' ? '#34d399' : s === 'sell' ? '#f87171' : '#fbbf24';

const levelColor = (s = '') => {
  const u = s.toUpperCase();
  if (u === 'BUY_STRONG') return '#22c55e';
  if (u === 'BUY') return '#34d399';
  if (u === 'BUY_WEAK') return '#86efac';
  if (u === 'SELL_STRONG') return '#dc2626';
  if (u === 'SELL') return '#f87171';
  if (u === 'SELL_WEAK') return '#fca5a5';
  return '#fbbf24';
};

const patternIcon = (type) => type === 'bullish' ? '▲' : type === 'bearish' ? '▼' : '◆';
const patternColor = (type) => type === 'bullish' ? '#34d399' : type === 'bearish' ? '#f87171' : '#fbbf24';

export default function AIInsights() {
  const [symbol, setSymbol] = useState('BTCUSDT');
  const [interval, setInterval] = useState('1h');

  const { data: prediction, isFetching: predFetching } = useQuery({
    queryKey: ['predict', symbol, interval],
    queryFn: () => aiApi.get(`/predict/${symbol}?interval=${interval}`).then(r => r.data),
    refetchInterval: 30_000,
  });

  const { data: techAnalysis, isFetching: taFetching } = useQuery({
    queryKey: ['analyze', symbol, interval],
    queryFn: () => aiApi.get(`/analyze/${symbol}?interval=${interval}&candles=150`).then(r => r.data),
    refetchInterval: 30_000,
  });

  const { data: signals = [] } = useQuery({
    queryKey: ['signals'],
    queryFn: () => aiApi.get('/signals').then(r => r.data),
    refetchInterval: 30_000,
  });

  const { data: cacheList = [] } = useQuery({
    queryKey: ['cache'],
    queryFn: () => aiApi.get('/cache').then(r => r.data),
    refetchInterval: 30_000,
  });

  const { data: modelsInfo } = useQuery({
    queryKey: ['models'],
    queryFn: () => aiApi.get('/models').then(r => r.data),
    refetchInterval: 30_000,
  });

  const { data: mtfPrediction } = useQuery({
    queryKey: ['mtf-predict', symbol],
    queryFn: () => aiApi.get(`/predict/mtf/${symbol}`).then(r => r.data),
    refetchInterval: 60_000,
    enabled: modelsInfo?.mtf?.trained,
  });

  const trainMutation = useMutation({
    mutationFn: () => aiApi.post(`/train/${symbol}?interval=${interval}&days=365`),
  });

  const [mtfDays1m, setMtfDays1m] = useState(7);

  const trainMtfMutation = useMutation({
    mutationFn: () => aiApi.post(`/train/mtf/${symbol}?days=365&days_1m=${mtfDays1m}`),
  });

  const backtestMutation = useMutation({
    mutationFn: () => aiApi.post(`/backtest/${symbol}?interval=${interval}&limit=1000`),
  });

  const bt = backtestMutation.data?.data;

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 24 }}>AI Insights</h1>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        {SYMBOLS.map(s => (
          <button key={s} onClick={() => setSymbol(s)} style={{
            background: symbol === s ? '#38bdf8' : '#1e293b',
            color: symbol === s ? '#0f172a' : '#94a3b8',
            border: 'none', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontWeight: 600,
          }}>{s}</button>
        ))}
        <div style={{ display: 'flex', gap: 6, marginLeft: 8 }}>
          {INTERVALS.map(i => (
            <button key={i} onClick={() => setInterval(i)} style={{
              background: interval === i ? '#334155' : 'transparent',
              color: interval === i ? '#e2e8f0' : '#64748b',
              border: '1px solid #334155', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12,
            }}>{i}</button>
          ))}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => trainMutation.mutate()} disabled={trainMutation.isPending} style={{
            background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: 8,
            padding: '8px 14px', cursor: 'pointer', fontSize: 13,
          }}>
            {trainMutation.isPending ? 'Treinando...' : `Treinar ${symbol} ${interval}`}
          </button>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <select value={mtfDays1m} onChange={e => setMtfDays1m(Number(e.target.value))}
              style={{ background: '#0f172a', color: '#fbbf24', border: '1px solid #fbbf24', borderRadius: 6, padding: '6px 8px', fontSize: 12, cursor: 'pointer' }}>
              <option value={7}>1m: 7 dias (rápido)</option>
              <option value={30}>1m: 30 dias</option>
              <option value={90}>1m: 90 dias</option>
              <option value={365}>1m: 1 ano (lento, ~10min)</option>
            </select>
            <button onClick={() => trainMtfMutation.mutate()} disabled={trainMtfMutation.isPending} style={{
              background: '#422006', color: '#fbbf24', border: '1px solid #fbbf24', borderRadius: 8,
              padding: '8px 14px', cursor: 'pointer', fontSize: 13,
            }}>
              {trainMtfMutation.isPending ? 'Treinando MTF...' : '🧠 Treinar MTF (geral)'}
            </button>
          </div>
          <button onClick={() => backtestMutation.mutate()} disabled={backtestMutation.isPending} style={{
            background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8,
            padding: '8px 14px', cursor: 'pointer', fontSize: 13,
          }}>
            {backtestMutation.isPending ? 'Calculando...' : 'Backtest'}
          </button>
        </div>
      </div>

      {/* Previsão atual */}
      {prediction && (
        <div style={card}>
          <h2 style={{ fontSize: 15, color: '#94a3b8', marginBottom: 16 }}>Previsão Atual · {symbol}</h2>
          <div style={{ display: 'flex', gap: 32, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 48, fontWeight: 800, color: signalColor(prediction.signal) }}>
                {prediction.signal?.toUpperCase()}
              </div>
              <div style={{ fontSize: 13, color: '#64748b' }}>Sinal</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 36, fontWeight: 700, color: '#e2e8f0' }}>
                {Math.round((prediction.confidence || 0) * 100)}%
              </div>
              <div style={{ fontSize: 13, color: '#64748b' }}>Confiança</div>
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>Indicadores Técnicos</div>
              {prediction.indicators && Object.entries(prediction.indicators).map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ color: '#94a3b8', fontSize: 13 }}>{k.toUpperCase()}</span>
                  <span style={{ color: '#e2e8f0', fontSize: 13 }}>{v !== null ? v : '—'}</span>
                </div>
              ))}
            </div>
            {prediction.probabilities && (
              <div style={{ minWidth: 180 }}>
                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>Probabilidades</div>
                {Object.entries(prediction.probabilities).map(([k, v]) => (
                  <div key={k} style={{ marginBottom: 6 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                      <span style={{ fontSize: 12, color: signalColor(k) }}>{k.toUpperCase()}</span>
                      <span style={{ fontSize: 12 }}>{Math.round(v * 100)}%</span>
                    </div>
                    <div style={{ background: '#0f172a', borderRadius: 4, height: 6 }}>
                      <div style={{ background: signalColor(k), width: `${Math.round(v * 100)}%`, height: '100%', borderRadius: 4, transition: 'width .3s' }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Technical Analysis — compound 7-level signal */}
      {techAnalysis && (
        <div style={{ ...card, borderLeft: '4px solid #818cf8' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14, color: '#818cf8', fontWeight: 700 }}>Análise Técnica Composta · {symbol} {interval}</span>
            {taFetching && <span style={{ fontSize: 11, color: '#64748b' }}>atualizando...</span>}
          </div>

          {/* Score + Signal */}
          <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap', marginBottom: 20 }}>
            <div style={{ textAlign: 'center', minWidth: 120 }}>
              <div style={{ fontSize: 42, fontWeight: 800, color: levelColor(techAnalysis.signal), lineHeight: 1.1 }}>
                {techAnalysis.signal}
              </div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>Sinal composto</div>
            </div>
            <div style={{ textAlign: 'center', minWidth: 80 }}>
              <div style={{ fontSize: 36, fontWeight: 700, color: techAnalysis.score >= 0 ? '#34d399' : '#f87171' }}>
                {techAnalysis.score > 0 ? '+' : ''}{techAnalysis.score}
              </div>
              <div style={{ fontSize: 11, color: '#64748b' }}>Score total</div>
            </div>
            {/* Score breakdown */}
            {techAnalysis.score_breakdown && (
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>Composição do score</div>
                {Object.entries(techAnalysis.score_breakdown).map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, fontSize: 12 }}>
                    <span style={{ color: '#94a3b8' }}>{k.replace(/_/g, ' ')}</span>
                    <span style={{ color: v > 0 ? '#34d399' : v < 0 ? '#f87171' : '#64748b', fontWeight: 600 }}>
                      {v > 0 ? '+' : ''}{v}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Candlestick Patterns */}
          {techAnalysis.components?.patterns?.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 700, marginBottom: 8 }}>Padrões de Candlestick detectados</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {techAnalysis.components.patterns.map((p, idx) => (
                  <div key={idx} style={{
                    background: '#0f172a', borderRadius: 8, padding: '6px 12px',
                    border: `1px solid ${patternColor(p.type)}44`,
                    fontSize: 12,
                  }}>
                    <span style={{ color: patternColor(p.type), marginRight: 6 }}>{patternIcon(p.type)}</span>
                    <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{p.name}</span>
                    <span style={{ color: '#64748b', marginLeft: 6 }}>{p.strength}</span>
                    <span style={{ color: p.score > 0 ? '#34d399' : p.score < 0 ? '#f87171' : '#fbbf24', marginLeft: 8, fontWeight: 700 }}>
                      {p.score > 0 ? '+' : ''}{p.score}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Indicators + Divergences */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 12 }}>
            {techAnalysis.components?.indicators && (() => {
              const ind = techAnalysis.components.indicators;
              return (
                <div style={{ background: '#0f172a', borderRadius: 8, padding: '10px 14px' }}>
                  <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 700, marginBottom: 8 }}>Indicadores</div>
                  {[
                    { k: 'RSI', v: `${ind.rsi ?? '—'} (${ind.rsi_signal ?? '—'})`, color: ind.rsi_signal === 'oversold' ? '#34d399' : ind.rsi_signal === 'overbought' ? '#f87171' : '#e2e8f0' },
                    { k: 'MACD', v: ind.macd_signal ?? '—', color: ind.macd_signal?.includes('bullish') ? '#34d399' : ind.macd_signal?.includes('bearish') ? '#f87171' : '#e2e8f0' },
                    { k: 'BB', v: ind.bb_signal ?? '—', color: ind.bb_signal === 'near_lower' ? '#34d399' : ind.bb_signal === 'near_upper' ? '#f87171' : '#e2e8f0' },
                  ].map(({ k, v, color }) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12 }}>
                      <span style={{ color: '#64748b' }}>{k}</span>
                      <span style={{ color, fontWeight: 600 }}>{v}</span>
                    </div>
                  ))}
                  <div style={{ fontSize: 11, color: '#475569', marginTop: 6 }}>
                    Limiares RSI: {ind.rsi_overbought}/{ind.rsi_oversold}
                  </div>
                </div>
              );
            })()}

            {techAnalysis.components?.divergence && (() => {
              const div = techAnalysis.components.divergence;
              return (
                <div style={{ background: '#0f172a', borderRadius: 8, padding: '10px 14px' }}>
                  <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 700, marginBottom: 8 }}>Divergências</div>
                  <div style={{ fontSize: 12, marginBottom: 4 }}>
                    <span style={{ color: '#64748b' }}>RSI: </span>
                    <span style={{ color: div.rsi_divergence === 'bullish' ? '#34d399' : div.rsi_divergence === 'bearish' ? '#f87171' : '#475569', fontWeight: 600 }}>
                      {div.rsi_divergence ?? 'nenhuma'}
                    </span>
                  </div>
                  <div style={{ fontSize: 12 }}>
                    <span style={{ color: '#64748b' }}>MACD: </span>
                    <span style={{ color: div.macd_divergence === 'bullish' ? '#34d399' : div.macd_divergence === 'bearish' ? '#f87171' : '#475569', fontWeight: 600 }}>
                      {div.macd_divergence ?? 'nenhuma'}
                    </span>
                  </div>
                </div>
              );
            })()}

            {techAnalysis.components?.volume && (() => {
              const vol = techAnalysis.components.volume;
              return (
                <div style={{ background: '#0f172a', borderRadius: 8, padding: '10px 14px' }}>
                  <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 700, marginBottom: 8 }}>Volume (OBV)</div>
                  {[
                    { k: 'Tendência OBV', v: vol.obv_trend },
                    { k: 'Ratio Volume', v: `${vol.volume_ratio}x` },
                    { k: 'Direção Preço', v: vol.price_direction },
                  ].map(({ k, v }) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12 }}>
                      <span style={{ color: '#64748b' }}>{k}</span>
                      <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{v}</span>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

          {/* Support / Resistance */}
          {techAnalysis.components?.support_resistance && (() => {
            const sr = techAnalysis.components.support_resistance;
            const price = techAnalysis.current_price;
            return (
              <div style={{ background: '#0f172a', borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 700, marginBottom: 8 }}>
                  Suporte / Resistência · atual: ${price?.toLocaleString()}
                </div>
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: 11, color: '#34d399', marginBottom: 4 }}>Suporte</div>
                    {sr.support?.slice(0, 3).map((s, i) => (
                      <div key={i} style={{ fontSize: 12, color: '#94a3b8', marginBottom: 2 }}>
                        ${s.price?.toLocaleString()} <span style={{ color: '#475569' }}>(força: {Math.round((s.strength || 0) * 100)}%)</span>
                      </div>
                    ))}
                    {sr.support?.length === 0 && <span style={{ fontSize: 11, color: '#475569' }}>nenhum detectado</span>}
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#f87171', marginBottom: 4 }}>Resistência</div>
                    {sr.resistance?.slice(0, 3).map((r, i) => (
                      <div key={i} style={{ fontSize: 12, color: '#94a3b8', marginBottom: 2 }}>
                        ${r.price?.toLocaleString()} <span style={{ color: '#475569' }}>(força: {Math.round((r.strength || 0) * 100)}%)</span>
                      </div>
                    ))}
                    {sr.resistance?.length === 0 && <span style={{ fontSize: 11, color: '#475569' }}>nenhum detectado</span>}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Sinais de todos os pares */}
      <div style={card}>
        <h2 style={{ fontSize: 15, color: '#94a3b8', marginBottom: 16 }}>Sinais — Todos os Pares</h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {signals.map(s => (
            <div key={s.symbol} style={{ background: '#0f172a', borderRadius: 10, padding: '12px 18px', border: '1px solid #334155', minWidth: 140 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>{s.symbol}</div>
              <div style={{ color: signalColor(s.signal), fontWeight: 700, fontSize: 16 }}>{s.signal?.toUpperCase()}</div>
              <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>
                {s.trained ? `${Math.round((s.confidence || 0) * 100)}% confiança` : 'Modelo não treinado'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* MTF prediction — general model */}
      {mtfPrediction && (
        <div style={{ ...card, borderLeft: '4px solid #fbbf24' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14, color: '#fbbf24', fontWeight: 700 }}>🧠 Modelo MTF — Aprendizado Geral</span>
            <span style={{ fontSize: 11, color: '#64748b', background: '#1e293b', padding: '2px 8px', borderRadius: 4 }}>
              {mtfPrediction.model || 'MTF'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div style={{ textAlign: 'center', minWidth: 100 }}>
              <div style={{ fontSize: 40, fontWeight: 800, color: signalColor(mtfPrediction.signal) }}>{mtfPrediction.signal?.toUpperCase()}</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#e2e8f0' }}>{Math.round((mtfPrediction.confidence || 0) * 100)}%</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>Confluência MTF</div>
            </div>
            <div style={{ flex: 1, minWidth: 260 }}>
              {/* Group indicators by timeframe */}
              {['1h', '1m', '5m', '15m', '4h', '1d'].map(tf => {
                const prefix = tf === '1h' ? '1h_' : `tf${tf}_`;
                const tfInds = mtfPrediction.indicators
                  ? Object.entries(mtfPrediction.indicators).filter(([k]) => k.startsWith(prefix))
                  : [];
                if (tfInds.length === 0) return null;
                return (
                  <div key={tf} style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 11, color: tf === '1h' ? '#38bdf8' : '#fbbf24', fontWeight: 700, marginBottom: 4 }}>
                      {tf === '1h' ? '1h (base)' : tf} timeframe
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 16px', fontSize: 12 }}>
                      {tfInds.map(([k, v]) => (
                        <div key={k} style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#64748b' }}>{k.replace(prefix, '').toUpperCase()}</span>
                          <b style={{ color: '#e2e8f0' }}>{v !== null ? v : '—'}</b>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {!modelsInfo?.mtf?.trained && (
            <div style={{ marginTop: 12, padding: '8px 12px', background: '#422006', borderRadius: 8, fontSize: 12, color: '#fbbf24' }}>
              ⚠ Modelo MTF não treinado ainda. Clique em "🧠 Treinar MTF (geral)" acima.
            </div>
          )}
        </div>
      )}

      {/* Trained models list */}
      {modelsInfo?.per_timeframe?.length > 0 && (
        <div style={card}>
          <h2 style={{ fontSize: 15, color: '#94a3b8', marginBottom: 12 }}>Modelos Treinados por Timeframe</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {modelsInfo.per_timeframe.map(m => (
              <div key={m.key} style={{
                background: m.trained ? '#052e16' : '#1e293b',
                border: `1px solid ${m.trained ? '#34d399' : '#334155'}`,
                borderRadius: 8, padding: '6px 14px', fontSize: 13,
              }}>
                <b style={{ color: '#e2e8f0' }}>{m.symbol}</b>
                <span style={{ color: '#64748b' }}> {m.interval}</span>
                <span style={{ color: m.trained ? '#34d399' : '#475569', marginLeft: 8 }}>{m.trained ? '✓' : '○'}</span>
              </div>
            ))}
            <div style={{
              background: modelsInfo.mtf?.trained ? '#422006' : '#1e293b',
              border: `1px solid ${modelsInfo.mtf?.trained ? '#fbbf24' : '#334155'}`,
              borderRadius: 8, padding: '6px 14px', fontSize: 13,
            }}>
              <b style={{ color: '#fbbf24' }}>MTF</b>
              <span style={{ color: '#64748b' }}> 1h+4h+1d</span>
              <span style={{ color: modelsInfo.mtf?.trained ? '#fbbf24' : '#475569', marginLeft: 8 }}>
                {modelsInfo.mtf?.trained ? '✓' : '○'}
              </span>
            </div>
          </div>
          <p style={{ fontSize: 12, color: '#475569', marginTop: 10 }}>
            Cada símbolo + timeframe tem seu próprio modelo independente. O MTF vê <b style={{color:'#fbbf24'}}>todos os timeframes simultaneamente</b> (1m→1d) e aprende confluência entre eles. É o modelo mais poderoso mas precisa de mais dados para treinar.
          </p>
        </div>
      )}

      {/* Cache status */}
      {cacheList.length > 0 && (
        <div style={card}>
          <h2 style={{ fontSize: 15, color: '#94a3b8', marginBottom: 12 }}>Cache de Dados Históricos</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {cacheList.map(c => (
              <div key={`${c.symbol}_${c.interval}`} style={{ background: '#0f172a', borderRadius: 8, padding: '10px 14px', border: '1px solid #334155', fontSize: 13 }}>
                <div style={{ fontWeight: 700, color: '#38bdf8' }}>{c.symbol} <span style={{ color: '#64748b', fontWeight: 400 }}>{c.interval}</span></div>
                <div style={{ color: '#94a3b8', marginTop: 2 }}>{c.rows?.toLocaleString()} candles · {c.size_kb} KB</div>
                <div style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>
                  {c.from?.slice(0, 10)} → {c.to?.slice(0, 10)}
                </div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 12, color: '#475569', marginTop: 10 }}>
            Retreino usa dados em cache — só busca candles novos da Binance.
          </p>
        </div>
      )}

      {/* Backtest results */}
      {bt && (
        <div style={card}>
          <h2 style={{ fontSize: 15, color: '#94a3b8', marginBottom: 16 }}>Backtest · {bt.symbol} · {bt.interval}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 20 }}>
            {[
              { label: 'Retorno Total', value: `${bt.total_return_pct}%`, color: bt.total_return_pct >= 0 ? '#34d399' : '#f87171' },
              { label: 'Win Rate', value: `${bt.win_rate_pct}%` },
              { label: 'Trades', value: bt.total_trades },
              { label: 'Max Drawdown', value: `${bt.max_drawdown_pct}%`, color: '#f87171' },
              { label: 'Sharpe Ratio', value: bt.sharpe_ratio },
              { label: 'Capital Final', value: `$${bt.final_value?.toLocaleString()}` },
            ].map(({ label, value, color = '#e2e8f0' }) => (
              <div key={label} style={{ background: '#0f172a', borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
              </div>
            ))}
          </div>
          {bt.equity_curve?.length > 0 && (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={bt.equity_curve}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="time" hide />
                <YAxis stroke="#64748b" tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', fontSize: 12 }}
                  formatter={(v) => [`$${v.toLocaleString()}`, 'Portfolio']} />
                <ReferenceLine y={bt.initial_capital} stroke="#334155" strokeDasharray="4 4" />
                <Line type="monotone" dataKey="value" stroke="#38bdf8" dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      )}
    </div>
  );
}
